import creditCardType from "credit-card-type";

export interface CardBrandColors {
  primary: string;
  secondary: string;
}

export interface CardBrandInfo {
  name: string; // Nome formatado para a API
  displayName: string; // Nome para exibição
  colors: CardBrandColors;
}

// Cores para cada bandeira (para o preview do cartão)
const brandColors = {
  Visa: {
    primary: "#1434CB",
    secondary: "#1A1F71",
  },
  Mastercard: {
    primary: "#EB001B",
    secondary: "#F79E1B",
  },
  Amex: {
    primary: "#006FCF",
    secondary: "#002663",
  },
  Elo: {
    primary: "#FFCB05",
    secondary: "#FF6B00",
  },
  Hipercard: {
    primary: "#DC143C",
    secondary: "#8B0000",
  },
  Diners: {
    primary: "#0079BE",
    secondary: "#003D82",
  },
  Discover: {
    primary: "#FF6000",
    secondary: "#000000",
  },
  JCB: {
    primary: "#0066CC",
    secondary: "#003366",
  },
  UnionPay: {
    primary: "#E21836",
    secondary: "#000000",
  },
  Maestro: {
    primary: "#0099DD",
    secondary: "#0066AA",
  },
  default: {
    primary: "#5A5B5F",
    secondary: "#3A3B3F",
  },
};

// Mapeamento do formato credit-card-type para o formato esperado pela API
export const brandMapping: Record<string, CardBrandInfo> = {
  visa: { name: "Visa", displayName: "Visa", colors: brandColors.Visa },
  "master-card": {
    name: "Master",
    displayName: "Mastercard",
    colors: brandColors.Mastercard,
  },
  mastercard: {
    name: "Master",
    displayName: "Mastercard",
    colors: brandColors.Mastercard,
  },
  "american-express": {
    name: "American Express",
    displayName: "American Express",
    colors: brandColors.Amex,
  },
  "american express": {
    name: "American Express",
    displayName: "American Express",
    colors: brandColors.Amex,
  },
  amex: {
    name: "American Express",
    displayName: "American Express",
    colors: brandColors.Amex,
  },
  elo: { name: "Elo", displayName: "Elo", colors: brandColors.Elo },
  hipercard: {
    name: "Hipercard",
    displayName: "Hipercard",
    colors: brandColors.Hipercard,
  },
  "diners-club": {
    name: "Diners Club",
    displayName: "Diners Club",
    colors: brandColors.Diners,
  },
  "diners club": {
    name: "Diners Club",
    displayName: "Diners Club",
    colors: brandColors.Diners,
  },
  diners: {
    name: "Diners Club",
    displayName: "Diners Club",
    colors: brandColors.Diners,
  },
  discover: {
    name: "Discover",
    displayName: "Discover",
    colors: brandColors.Discover,
  },
  jcb: { name: "JCB", displayName: "JCB", colors: brandColors.JCB },
  unionpay: {
    name: "UnionPay",
    displayName: "UnionPay",
    colors: brandColors.UnionPay,
  },
  maestro: {
    name: "Master",
    displayName: "Maestro",
    colors: brandColors.Maestro,
  },
};

/**
 * Detecta a bandeira do cartão de crédito baseado no número
 *
 * @param cardNumber Número do cartão (pode conter espaços)
 * @returns Informações da bandeira ou null se não identificada
 */
export const detectCardBrand = (cardNumber: string): CardBrandInfo | null => {
  const cleanNumber = cardNumber.replace(/\D/g, "");
  if (cleanNumber.length < 1) {
    return null;
  }

  let cardType;
  try {
    cardType = creditCardType(cleanNumber);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Erro ao detectar bandeira do cartão:", error);
    return null;
  }

  if (!cardType || cardType.length === 0) {
    return null;
  }

  const type = cardType[0];
  if (!type) {
    return null;
  }
  const typeName = type.type || type.niceType;

  if (!typeName) {
    return null;
  }

  const normalizedTypeName = typeName.toLowerCase().trim().replace(/\s+/g, "-");
  const mapping =
    brandMapping[typeName] ||
    brandMapping[normalizedTypeName] ||
    brandMapping[typeName.toLowerCase()] ||
    brandMapping[typeName.replace(/\s+/g, "-")];

  if (!mapping) {
    return null;
  }

  const colors = mapping.colors || brandColors.default;

  return {
    name: mapping.name,
    displayName: mapping.displayName,
    colors,
  };
};
